import { useManageAppointmentContext } from '../lib/context/ManageAppointmentContext';
import { Appointment } from '../schemas/shared';
import { ActionItem } from '../components/Table/TableDataView';
import useIsClientAdmin from './useIsClientAdmin';
import useIsReferrerAdmin from './useIsReferrerAdmin';
import { infoModal, errorModal } from '../components/Modals';
import VoiceMediaTable from '../components/Appointments/TwillioEvents/VoiceMediaTable';
import VideoMediaTable from '../components/Appointments/TwillioEvents/VideoMediaTable';
import { TwilioEventsTable } from '../components/Appointments/TwillioEvents/TwillioEventsTable';
import React, { useCallback } from 'react';
import useApi from './useApi';
import useIsReferrer from './useIsReferrer';
import useIsAuditReferrer from './useIsAuditReferrer';
import i18n from 'vl-common/src/lib/i18n';

export const CaseReviewApptTypes = {
  CASEREV: 'CASEREV',
  CASEREVF: 'CASEREVF',
  CASEREV2NDOPIN: 'CASEREV2NDOPIN',
  CASEREVPIFU: 'CASEREVPIFU',
  CASEREVADD: 'CASEREVADD'
} as const;

export const CaseReviewApptTypeList: readonly string[] = Object.values(CaseReviewApptTypes);

const t = i18n.getFixedT(null, 'common');

const displayTwilioEvents = (Api: any, data: any, consumed: any) => {
  Api.user
    .getTwilioEvents({ appt_id: data.appointment_id })
    .then((res: any) => {
      if (res.status === 200 || res.status === 204) {
        const { events = [] } = res.data;

        events.reverse();

        infoModal({
          title: 'Twilio Events',
          maskClosable: true,
          content: (
            <div>
              <h3 style={{ marginTop: '1.5rem' }}>{t('Voice Media')}</h3>
              <VoiceMediaTable events={events} />
              <h3 style={{ marginTop: '1.5rem' }}>{t('Video Media')}</h3>
              <VideoMediaTable events={events} />
              <h3 style={{ marginTop: '1.5rem' }}>{t('All Events')}</h3>
              <TwilioEventsTable events={events} />
            </div>
          ),
          onOk() {},
          width: '90vw'
        });
      }
    })
    .catch((error: any) => {
      consumed(error);

      const serverMessage = error.response?.data?.message || error.response?.data?.error;
      const errorMessage = serverMessage || 'Twilio events data is unavailable';

      errorModal({
        title: 'Failed to load Twilio events',
        content: errorMessage,
        onOk() {}
      });
    });
};

type UseAppointmentManagementActionsParams = {
  refetchFn?: () => void;
};

const useAppointmentManagementActions = (params?: UseAppointmentManagementActionsParams) => {
  const { Api, consumed } = useApi();
  const isClientAdmin = useIsClientAdmin();
  const isReferrerAdmin = useIsReferrerAdmin();
  const isReferrer = useIsReferrer();
  const isAuditReferrer = useIsAuditReferrer();

  const {
    onSetActionedAppointment,
    openAddNotesModal,
    openViewNotesModal,
    openCancelAppointmentModal,
    openCallTypeChangeModal,
    openRescheduleAppointmentModal,
    openAddF2FReferralModal
  } = useManageAppointmentContext();

  const onResendGDRP = useCallback(
    async (data: { appointment_id: number }) => {
      const params = {
        appt_id: data.appointment_id,
        process_code: 'PATIENT_REFERRAL',
        reason_code: 'REQ_CLINICIAN'
      };
      try {
        await Api.gdpr.putGDPR(params);
      } catch (e) {
        console.log(e);
      }
    },
    [Api.gdpr]
  );

  const withActioned = useCallback(
    (appointment: Appointment, fn: () => void) => () => {
      onSetActionedAppointment({ appointment, refetchFn: params?.refetchFn });
      fn();
    },
    [onSetActionedAppointment, params?.refetchFn]
  );

  const generateAppointmentActions = useCallback(
    (appointmentData: Appointment & { [key: string]: unknown }): ActionItem[] => {
      const isCancelled = !!appointmentData.cancelled_date;
      const isCompleted = appointmentData.appointment_completed;
      const isActive = !isCancelled && !isCompleted;
      const isUpcoming = new Date(appointmentData.datetime) > new Date();
      const isCaseReview = CaseReviewApptTypeList.includes(appointmentData?.appt_type_code);

      return [
        {
          label: 'Add Note',
          onClick: withActioned(appointmentData, openAddNotesModal)
        },
        {
          label: 'View Notes',
          onClick: withActioned(appointmentData, openViewNotesModal)
        },
        { divider: true },
        {
          label: 'Resend GDPR Request',
          onClick: () => onResendGDRP(appointmentData),
          isVisible: () => {
            if (isActive) return false;
            return appointmentData.gdpr_resend && isClientAdmin;
          }
        },
        {
          label: 'Add F2F Referral',
          onClick: withActioned(appointmentData, openAddF2FReferralModal),
          isVisible: () => {
            if (!isCompleted || !isClientAdmin) return false;
            return !appointmentData.patient_nhs_client;
          }
        },
        {
          label: 'Reschedule Appointment',
          isVisible: () => {
            if (!isActive || isAuditReferrer) return false;
            return !isCaseReview;
          },
          isDisabled: () => Boolean(appointmentData?.audio_call_occurred || appointmentData?.video_call_occured),
          onClick: withActioned(appointmentData, openRescheduleAppointmentModal)
        },
        {
          label: 'Change Call Type',
          isVisible: () => {
            if (!isActive || !isClientAdmin) return false;
            return !isCaseReview;
          },
          onClick: withActioned(appointmentData, openCallTypeChangeModal)
        },
        {
          label: 'Cancel Appointment',
          isVisible: () => {
            if (!isActive) return false;
            if (isAuditReferrer) return false;
            if (!isClientAdmin && !isUpcoming) return false;

            if (isClientAdmin) return true;

            return !appointmentData.appointment_refer && (isReferrerAdmin || isReferrer);
          },
          onClick: withActioned(appointmentData, openCancelAppointmentModal)
        },
        {
          label: 'Display TwilioEvents',
          isVisible: () => isClientAdmin && !appointmentData.cancelled_date,
          onClick: () => displayTwilioEvents(Api, appointmentData, consumed)
        }
      ];
    },
    [
      Api,
      consumed,
      isAuditReferrer,
      isClientAdmin,
      isReferrer,
      isReferrerAdmin,
      onResendGDRP,
      openAddNotesModal,
      openViewNotesModal,
      openAddF2FReferralModal,
      openRescheduleAppointmentModal,
      openCallTypeChangeModal,
      openCancelAppointmentModal,
      withActioned
    ]
  );

  return { generateAppointmentActions };
};

export default useAppointmentManagementActions;
